import { Select } from 'antd';
import React, { useEffect, useMemo } from 'react';
import { useGetCalculableMetrics } from '@src/emp-3.0/api/queryHooks';
import { MetricDataScope } from '@src/emp-3.0/types';
import Highlighter from 'react-highlight-words';
import { MetricRole } from './service';

interface IProps {
  metricRole: MetricRole;
  dataScope?: MetricDataScope[];
  value?: string;
  // raw event will be passed event name as string, mapped event will be passed event id as number
  onChange?: (value: number | string | null | (number | string)[], event?: any) => void;
  disabledOptions?: number[];
  defaultSelectedValue?: (number | string)[];
}
export const CalculableMetricSelect: React.FC<IProps> = ({
  metricRole,
  dataScope,
  value,
  onChange,
  disabledOptions,
  defaultSelectedValue,
  ...rest
}) => {
  const { data: calculableMetrics = [], isLoading } = useGetCalculableMetrics();
  const [open, setOpen] = React.useState(false);
  const [selectedValues, setSelectedValues] = React.useState<(number | string)[]>([]);
  const [searchText, setSearchText] = React.useState('');
  useEffect(() => {
    if (defaultSelectedValue) {
      setSelectedValues(defaultSelectedValue);
    }
  }, [defaultSelectedValue]);

  const options = useMemo(() => {
    const validData = calculableMetrics.filter((it) => {
      const { is_predefined, target_scope } = it;
      if (metricRole !== 'denominator' && is_predefined) {
        return false;
      }
      if (!dataScope) {
        return true;
      }
      return target_scope.some((it) => dataScope.includes(it));
    });
    const options = validData
      .filter((event) => event.name.toLowerCase().includes(searchText.toLowerCase()))
      .map((event, index) => {
        const optionValue = event.id || event.name;
        const disabled =
          disabledOptions?.includes(event.id) || selectedValues.includes(optionValue);
        const key = `${event.id}_${index}`;
        return {
          label: (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                justifyContent: 'space-between',
              }}
            >
              <Highlighter
                autoEscape
                textToHighlight={event.name}
                highlightStyle={{
                  mixBlendMode: 'darken',
                  fontWeight: 500,
                }}
                searchWords={['']}
              />
              {/* <span>
                {event.target_scope ? (
                  <span style={{ color: 'rgba(0,0,0,.45)', display: 'inline-block' }}>
                    {event.target_scope.join(', ')}
                  </span>
                ) : (
                  ''
                )}
              </span> */}
            </div>
          ),
          text: event.name,
          value: optionValue,
          key,
          disabled,
          event,
        };
      });
    return options.sort((a, b) => Number(a.disabled) - Number(b.disabled));
  }, [calculableMetrics, metricRole, dataScope, searchText, disabledOptions, selectedValues]);

  /** if current value is not a member of options, change it to null */
  useEffect(() => {
    if (!value || !Array.isArray(options) || options.length === 0) {
      return;
    }
    const exist = options.some((it) => it.value === value);
    if (!exist) {
      onChange?.(null);
    }
  }, [value, options, onChange]);
  return (
    <Select
      loading={isLoading}
      style={{ flex: '1 0 0', minWidth: '300px' }}
      placeholder={'Select existing metric'}
      options={options}
      showSearch
      allowClear={true}
      searchValue={searchText}
      value={value}
      {...rest}
      open={open}
      onSelect={(value, option) => {
        if (typeof value === 'string') {
          // raw event
          onChange?.(value, value);
        } else {
          onChange?.(value, option.event);
        }
      }}
      onClear={() => {
        onChange?.(null, null);
      }}
      onSearch={(val) => setSearchText(val)}
      onOpenChange={(open) => {
        if (!open) {
          setSearchText('');
        }
        setOpen(open);
      }}
      filterOption={false}
    />
  );
};
